import styled from '@emotion/styled'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import { isEmpty, values } from 'ramda'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import { FieldSpec } from '@/app/types'

import ContactsUploadConfigureActions from './steps/configure/actions'
import ContactsUploadConfigureContent from './steps/configure/content'
import ContactsUploadCountriesActions from './steps/countries/actions'
import ContactsUploadCountriesContent from './steps/countries/content'
import ContactsUploadReviewActions from './steps/review/actions'
import ContactsUploadReviewContent from './steps/review/content'
import ContactsUploadSuccessActions from './steps/success/actions'
import ContactsUploadSuccessContent from './steps/success/content'
import ContactsUploadInitActions from './steps/upload/actions'
import ContactsUploadInitContent from './steps/upload/content'
import type {
  CountryValue,
  UploadConfigs,
  UploadConfirmItem,
  UploadData,
  UploadError,
  UploadFilePayload,
  UploadFileState,
} from './types'

type Props = {
  classes?: Record<string, string>
  countriesList: Record<string, string>
  loading?: boolean
  orgId: number
  page: number
  size: number
  specs: Record<string, FieldSpec>
  token: string
  uploadConfirmError: UploadError
  uploadData?: UploadData
  uploadError: UploadError

  confirmUpload: (args: { token: string; orgId: number; item: UploadConfirmItem }) => Promise<UploadConfirmItem | Error>
  getUploadStatus: (args: {
    token: string
    orgId: number
    uploadKey: string
    query: { page: number; size: number }
  }) => Promise<void | Error>
  uploadContacts: (args: {
    token: string
    orgId: number
    file: UploadFilePayload | File
  }) => Promise<UploadData | Error>
}

type State = {
  activeStep: number
  columnConfig: UploadConfigs | null
  columnErrors: string[]
  columnStatus: boolean[]
  countriesOption: 'single' | 'multi'
  countryCodeError: string
  countryValue: CountryValue
  dropError: UploadError
  error: Record<string, unknown>
  file: UploadFileState
  fileName: string
  loading: boolean
}

const emptyConfigs: UploadConfigs = {
  fieldsToColumns: {},
  customFieldsToColumns: {},
  sampleData: [],
  titlesFromFile: [],
}

const stepTitles = ['Upload', 'Select country', 'Configure', 'Review', 'Finish'] as const

const ContactsUpload: React.FC<Props> = (props) => {
  const navigate = useNavigate()

  const {
    countriesList,
    loading,
    orgId,
    page,
    size,
    specs,
    token,
    uploadConfirmError,
    uploadData,
    uploadError,
    confirmUpload,
    getUploadStatus,
    uploadContacts,
  } = props

  const [state, setState] = React.useState<State>({
    fileName: '',
    file: null,
    activeStep: 0,
    dropError: {},
    countriesOption: 'single',
    countryValue: {
      input: '',
      code: undefined,
    },
    countryCodeError: '',
    columnConfig: null,
    columnStatus: [],
    columnErrors: [],
    loading: false,
    error: {},
  })

  React.useEffect(() => {
    if (!uploadData) {
      return
    }

    setState((prev) => ({
      ...prev,
      columnConfig: uploadData,
      columnStatus: !isEmpty(uploadData) ? uploadData.titlesFromFile.map(() => true) : prev.columnStatus,
      columnErrors: !isEmpty(uploadData) ? uploadData.titlesFromFile.map(() => '') : prev.columnErrors,
    }))
  }, [uploadData])

  const stepForwardHandler = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeStep: Math.min(prev.activeStep + 1, 4),
    }))
  }, [])

  const stepBackHandler = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: {},
      loading: false,
      activeStep: Math.max(0, prev.activeStep - 1),
    }))
  }, [])

  const submitUploadHandler = React.useCallback(() => {
    const { file } = state

    if (!file) {
      return
    }

    uploadContacts({
      token,
      orgId,
      file,
    }).then((res) => {
      if (!(res instanceof Error)) {
        stepForwardHandler()
      }
    })
  }, [orgId, state, stepForwardHandler, token, uploadContacts])

  const submitCountriesHandler = React.useCallback(() => {
    stepForwardHandler()
  }, [stepForwardHandler])

  const submitConfigureHandler = React.useCallback(
    (currentState: State) => {
      const { columnConfig, columnStatus, columnErrors } = currentState

      if (!columnConfig) {
        return
      }

      if (columnErrors.find((error) => error !== '')) {
        return
      }

      const errors = columnConfig.titlesFromFile.map((_, i) => {
        if (
          columnStatus[i] &&
          values(columnConfig.fieldsToColumns).indexOf(i.toString()) === -1 &&
          values(columnConfig.customFieldsToColumns).indexOf(i.toString()) === -1
        ) {
          return 'Select a value or disable column'
        }
        return ''
      })

      if (errors.find((error) => error !== '')) {
        setState((prev) => ({
          ...prev,
          columnErrors: errors,
        }))
        return
      }

      stepForwardHandler()
    },
    [stepForwardHandler],
  )

  const submitReviewHandler = React.useCallback(
    (item: UploadConfirmItem) => {
      if (!uploadData) {
        return Promise.resolve()
      }

      return confirmUpload({
        token,
        orgId,
        item,
      }).then((res) => {
        if (res && !(res instanceof Error)) {
          stepForwardHandler()
          getUploadStatus({
            token,
            orgId,
            uploadKey: uploadData.uploadKey,
            query: {
              page: 1,
              size,
            },
          })
        }
      })
    },
    [confirmUpload, getUploadStatus, orgId, size, stepForwardHandler, token, uploadData],
  )

  const closeHandler = React.useCallback(() => {
    navigate(`/contacts/page/${page}`)
  }, [navigate, page])

  const isLoading = Boolean(loading)
  const { activeStep } = state

  const renderContent = () => {
    if (activeStep === 0) {
      return (
        <ContactsUploadInitContent
          error={{
            ...uploadError,
            ...state.dropError,
          }}
          file={state.file}
          fileName={state.fileName}
          onAccept={({
            file,
            fileName,
            error = {},
          }: {
            file: UploadFilePayload
            fileName: string
            error?: UploadError
          }) =>
            setState((prev) => ({
              ...prev,
              dropError: error,
              file,
              fileName,
            }))
          }
          onDelete={() =>
            setState((prev) => ({
              ...prev,
              dropError: {},
              file: null,
              fileName: '',
            }))
          }
        />
      )
    }

    if (activeStep === 1) {
      return (
        <ContactsUploadCountriesContent
          countriesList={countriesList}
          countriesOption={state.countriesOption}
          countryValue={state.countryValue}
          countryCodeError={state.countryCodeError}
          onCountryOptionChange={({ value, error = '' }: { value: 'single' | 'multi'; error?: string }) =>
            setState((prev) => ({
              ...prev,
              error: {},
              countriesOption: value,
              countryCodeError: error,
              countryValue: value === 'single' ? prev.countryValue : { input: '', code: undefined },
            }))
          }
          onCountryCodeChange={({ value, error }: { name?: string; value: CountryValue; error?: string }) =>
            setState((prev) => ({
              ...prev,
              error: {},
              countryValue: value,
              countryCodeError: error ?? '',
            }))
          }
        />
      )
    }

    if (activeStep === 2) {
      return (
        <ContactsUploadConfigureContent
          specs={specs}
          statuses={state.columnStatus}
          configs={state.columnConfig ?? emptyConfigs}
          errors={state.columnErrors}
          onConfigChange={({ errors, configs }) => {
            setState((prev) => ({
              ...prev,
              columnErrors: errors,
              columnConfig: configs,
            }))
          }}
          onStatusChange={({ errors, statuses }) => {
            setState((prev) => ({
              ...prev,
              columnErrors: errors,
              columnStatus: statuses,
            }))
          }}
        />
      )
    }

    if (activeStep === 3) {
      return <ContactsUploadReviewContent specs={specs} statuses={state.columnStatus} configs={state.columnConfig} />
    }

    return <ContactsUploadSuccessContent error={uploadConfirmError} />
  }

  const renderActions = () => {
    if (activeStep === 0) {
      return (
        <ContactsUploadInitActions
          submitHandler={submitUploadHandler}
          isSubmitDisabled={!state.file || state.loading}
        />
      )
    }

    if (activeStep === 1) {
      return (
        <ContactsUploadCountriesActions
          backHandler={stepBackHandler}
          submitHandler={submitCountriesHandler}
          isSubmitDisabled={
            state.countryCodeError !== '' ||
            (state.countriesOption === 'single' && state.countryValue.code === undefined) ||
            state.loading
          }
        />
      )
    }

    if (activeStep === 2) {
      return (
        <ContactsUploadConfigureActions
          backHandler={stepBackHandler}
          submitHandler={() => submitConfigureHandler(state)}
          isSubmitDisabled={
            !state.columnConfig || isEmpty(state.columnConfig) || !!state.columnErrors.find((err) => err !== '')
          }
        />
      )
    }

    if (activeStep === 3) {
      return (
        <ContactsUploadReviewActions
          specs={specs}
          statuses={state.columnStatus}
          configs={state.columnConfig}
          backHandler={stepBackHandler}
          submitHandler={(item) =>
            submitReviewHandler({
              ...item,
              regionCodeISO: state.countryValue.code,
            })
          }
        />
      )
    }

    return <ContactsUploadSuccessActions closeHandler={closeHandler} />
  }

  return (
    <Wrap>
      <Loading isLoading={isLoading}>
        <CardContent>
          <StyledStepper activeStep={activeStep} alternativeLabel={isAlternativeLabel()}>
            {stepTitles.map((title) => (
              <Step key={title}>
                <StepLabel>{getStepTitle(title)}</StepLabel>
              </Step>
            ))}
          </StyledStepper>
          {renderContent()}
        </CardContent>
        <CardActions>{renderActions()}</CardActions>
      </Loading>
    </Wrap>
  )
}

const isAlternativeLabel = () => window.innerWidth < 750

const getStepTitle = (title: string) => (window.innerWidth < 480 ? '' : title)

const Wrap = styled('div')({
  width: '100%',
  outline: 'none',
})

const StyledStepper = styled(Stepper)({
  padding: '24px 0',
})

export default ContactsUpload
